import React, { useState, useCallback } from 'react';
import { GRID_SIZE, ENABLE_PROTESTS } from '../constants/config';
import { scoreToDotColor, scoreToCellBackground } from '../utils/scoreColors';
import { generateShareText } from '../utils/generateShareText';
import DefinitionPopover from './DefinitionPopover';

export default function ScoreDisplay({
  grid,
  totalScore,
  leaderboardData,
  onProtest,
  onFinish,
  puzzle,
  retryCount,
  protestResults,
}) {
  const [copied, setCopied] = useState(false);
  const [activeDefinition, setActiveDefinition] = useState(null);

  // Can protest if the feature is enabled, under limit (2), and either no
  // protests yet or the last was accepted. ENABLE_PROTESTS gates the whole
  // affordance: instruction text, clickable cells, and hover/title cues.
  const canProtest = ENABLE_PROTESTS &&
    (!protestResults || protestResults.length < 2) &&
    (!protestResults || protestResults.length === 0 || protestResults[protestResults.length - 1]?.accepted);

  const toggleDefinition = (key) => {
    setActiveDefinition((prev) => (prev === key ? null : key));
  };

  const handleShare = useCallback(() => {
    const text = generateShareText(grid, totalScore, puzzle.puzzleNumber, retryCount, protestResults);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [grid, totalScore, puzzle.puzzleNumber, retryCount, protestResults]);

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white border-2 border-ink p-4 sm:p-8 max-w-lg w-full">
        <h2 className="text-3xl font-serif font-black text-center text-ink mb-6">
          {totalScore.toFixed(1)}
        </h2>

        {leaderboardData && (
          <div className="border border-ink-faint p-4 mb-6 text-center">
            <p className="text-sm text-ink font-body font-semibold">
              Better than {leaderboardData.percentile}% of players
            </p>
            <p className="text-xs text-ink-light mt-1 font-body">
              Rank #{leaderboardData.rank} of {leaderboardData.totalPlayers}
            </p>
          </div>
        )}

        {/* Protest instruction */}
        {canProtest && (
          <p className="text-xs text-ink text-center mb-3 font-body">
            <span className="font-bold">Experimental:</span> Click a square below to protest its score
          </p>
        )}

        {/* Grid with adjective labels + definition icons */}
        <div className="mb-6">
          <div className="grid gap-1 sm:gap-1.5" style={{ gridTemplateColumns: `3rem repeat(${GRID_SIZE}, 1fr)` }}>
            {/* Empty top-left corner */}
            <div />
            {/* Column headers */}
            {puzzle.colAdjectives.map((adj, i) => (
              <div
                key={`col-${i}`}
                className="text-center text-xs font-serif font-bold text-ink py-1 relative"
              >
                <span>{adj}</span>
                <button
                  onClick={() => toggleDefinition(`col-${i}`)}
                  className="text-ink-faint hover:text-ink text-[10px] cursor-pointer ml-0.5"
                  aria-label={`Definition of ${adj}`}
                  aria-expanded={activeDefinition === `col-${i}`}
                >
                  &#9432;
                </button>
                {activeDefinition === `col-${i}` && (
                  <DefinitionPopover
                    word={adj}
                    definition={puzzle.definitions?.[adj]}
                    onClose={() => setActiveDefinition(null)}
                    position="below"
                  />
                )}
              </div>
            ))}

            {/* Rows with row header + cells */}
            {Array.from({ length: GRID_SIZE }).map((_, rowIdx) => (
              <React.Fragment key={`row-${rowIdx}`}>
                <div className="flex flex-col items-center justify-center text-xs font-serif font-bold text-ink pr-1 relative">
                  <span>{puzzle.rowAdjectives[rowIdx]}</span>
                  <button
                    onClick={() => toggleDefinition(`row-${rowIdx}`)}
                    className="text-ink-faint hover:text-ink text-[10px] cursor-pointer"
                    aria-label={`Definition of ${puzzle.rowAdjectives[rowIdx]}`}
                    aria-expanded={activeDefinition === `row-${rowIdx}`}
                  >
                    &#9432;
                  </button>
                  {activeDefinition === `row-${rowIdx}` && (
                    <DefinitionPopover
                      word={puzzle.rowAdjectives[rowIdx]}
                      definition={puzzle.definitions?.[puzzle.rowAdjectives[rowIdx]]}
                      onClose={() => setActiveDefinition(null)}
                      position="right"
                    />
                  )}
                </div>
                {Array.from({ length: GRID_SIZE }).map((_, colIdx) => {
                  const cell = grid[rowIdx][colIdx];

                  return (
                    <button
                      key={`cell-${rowIdx}-${colIdx}`}
                      onClick={() => canProtest && onProtest(rowIdx, colIdx)}
                      disabled={!canProtest}
                      className={`p-2 text-center transition-transform ${
                        canProtest ? 'hover:scale-105 cursor-pointer' : 'cursor-default'
                      }`}
                      style={{ backgroundColor: scoreToCellBackground(cell.finalScore) }}
                      title={
                        canProtest
                          ? `Click to protest: ${cell.finalWord}`
                          : undefined
                      }
                    >
                      <p className="font-serif font-bold text-sm truncate">{cell.finalWord}</p>
                      <p
                        className="text-xs font-body font-semibold text-white inline-block px-1.5 py-0.5 rounded-full mt-0.5"
                        style={{ backgroundColor: scoreToDotColor(cell.finalScore) }}
                      >
                        {cell.finalScore.toFixed(1)}
                      </p>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Retry & protest summary */}
        {(retryCount > 0 || (protestResults && protestResults.length > 0)) && (
          <div className="flex flex-wrap justify-center gap-3 mb-4 text-xs font-body">
            {retryCount > 0 && (
              <span className="text-ink-faint">{'\uD83D\uDD04'} {retryCount}</span>
            )}
            {protestResults && protestResults.map((pr, i) => (
              <span
                key={i}
                className={`px-2 py-0.5 rounded-full ${
                  pr.accepted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {'\u2696\uFE0F'} {pr.accepted ? `Protest accepted: +${pr.scoreChange.toFixed(1)}` : 'Protest denied'}
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleShare}
            className="flex-1 py-3 bg-white text-ink font-serif font-bold border-2 border-ink hover:bg-gray-50 transition-colors"
          >
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button
            onClick={onFinish}
            className="flex-1 py-3 bg-ink text-cream font-serif font-bold border-2 border-ink hover:bg-ink-light transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
